// @ts-nocheck
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { useDropzone } from 'react-dropzone';
import {
  SolyticsBox,
  SolyticsButton,
  SolyticsDivider,
  SolyticsDropdownInput,
  SolyticsIconButton,
  SolyticsTypography,
} from '../../../../package/components';

import {
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
  MAX_IMAGE_SIZE,
} from '../../../../const/CommonConst';
import { ReactComponent as SendIcon } from '../../../../assets/Icons/ChatIcons/send.svg';
import { ReactComponent as AddIcon } from '../../../../assets/Icons/QueryTracker/AddIcon.svg';
import { ReactComponent as UndoIcon } from '../../../../package/assets/Editor/Undo.svg';
import { ReactComponent as TextBoxIcon } from '../../../../package/assets/Editor/TextBox.svg';
import { ReactComponent as BoldIcon } from '../../../../package/assets/Editor/Bold.svg';
import { ReactComponent as ItalicIcon } from '../../../../package/assets/Editor/Italic.svg';
import { ReactComponent as UnderlineIcon } from '../../../../package/assets/Editor/Underline.svg';
import { ReactComponent as AlignCenterIcon } from '../../../../package/assets/Editor/AlignCenter.svg';
import { ReactComponent as AlignRightIcon } from '../../../../package/assets/Editor/AlignRight.svg';
import { ReactComponent as AlignLeftIcon } from '../../../../package/assets/Editor/AlignLeft.svg';
import { ReactComponent as OrderedListIcon } from '../../../../package/assets/Editor/OrderedList.svg';
import { ReactComponent as BulletListIcon } from '../../../../package/assets/Editor/BulletList.svg';
import { ReactComponent as AttachmentIcon } from '../../../../package/assets/Editor/Attachment.svg';
import { ReactComponent as CollapseChevronIcon } from '../../../../package/assets/Editor/CollapseChevron.svg';
import swalToast from '../../swalToast';

const BLOCK_TYPES = [
  {
    label: 'Normal text',
    cmd: (editor) => editor.chain().focus().setParagraph().run(),
    isActive: (editor) =>
      editor.isActive('paragraph') && !editor.isActive('heading'),
  },
  {
    label: 'Heading 1',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
  },
  {
    label: 'Heading 2',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
  },
  {
    label: 'Heading 3',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    label: 'Heading 4',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 4 }),
  },
  {
    label: 'Heading 5',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 5 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 5 }),
  },
  {
    label: 'Heading 6',
    cmd: (editor) => editor.chain().focus().toggleHeading({ level: 6 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 6 }),
  },
];

const FONT_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 28, 32];
const IMAGE_ACCEPT = {
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
  'image/jpeg': ['.jpeg', '.jpg'],
};
const IMAGE_ACCEPT_ATTR =
  '.png,.svg,.jpeg,.jpg,image/png,image/svg+xml,image/jpeg';

const EditorToolBar = ({
  editor,
  handleAttachFile,
  isEditorEmpty,
  handleSubmit,
  mode = 'default',
  cancelButton = null,
  submitButton = null,
  isUserAuthorizedForAddNote,
  showAttachment = false,
  hideInsertImageButton = false,
  allowStandalone = false,
  onInsertTextBox,
  onImageSelected,
  onTextAlignChange,
  onFontSizeChange,
  onToggleBold,
  onToggleItalic,
  onToggleUnderline,
  onToggleOrderedList,
  onToggleBulletList,
  onTextTypeChange,
  activeTextStyle,
  showCollapseControl = false,
  isToolbarCollapsed = false,
  onToggleToolbarCollapse,
  isFormattingDisabled = false,
  showListControls = false,
  onUndo,
  onRedo,
}) => {
  const [textType, setTextType] = useState(BLOCK_TYPES[0]);
  const [fontSize, setFontSize] = useState(16);
  const imageInputRef = useRef(null);
  const theme = useTheme();
  const canRenderWithoutEditor =
    allowStandalone || Boolean(onInsertTextBox) || Boolean(onImageSelected);

  const {
    acceptedFiles: identifyingDocumentAcceptedFiles,
    fileRejections: identifyingDocumentRejectedFiles,
    getRootProps: addAttachmentsGetRootProps,
    getInputProps: addAttachmentsGetInputProps,
    open: openAddAttachments,
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'text/plain': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    },
  });

  const handleImageIconClick = useCallback(() => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  }, []);

  useEffect(() => {
    if (identifyingDocumentRejectedFiles?.length > 0) {
      swalToast({
        icon: 'error',
        title:
          'Please upload a document with a valid format. Accepted formats include .pdf, .xlsx, .xls, .txt',
      });
      return;
    }
    if (identifyingDocumentAcceptedFiles?.length > 0) {
      handleAttachFile(identifyingDocumentAcceptedFiles);
    }
  }, [
    identifyingDocumentAcceptedFiles,
    identifyingDocumentRejectedFiles,
    handleAttachFile,
  ]);

  // Sync toolbar text type indicator with editor state
  useEffect(() => {
    if (!editor) {
      return;
    }
    const active = BLOCK_TYPES.find((t) => t.isActive(editor));
    if (active && active.label !== textType.label) {
      setTextType(active);
    }
  }, [editor?.state, textType.label]);

  useEffect(() => {
    if (Number.isFinite(activeTextStyle?.fontSize)) {
      setFontSize(Number(activeTextStyle?.fontSize));
    }
  }, [activeTextStyle?.fontSize]);

  const handleTextTypeChange = useCallback(
    (event) => {
      const selectedLabel = event?.target?.value;
      const selectedType =
        BLOCK_TYPES.find((type) => type.label === selectedLabel) ||
        BLOCK_TYPES[0];
      setTextType(selectedType);
      if (editor) {
        selectedType.cmd(editor);
      }
      if (onTextTypeChange) {
        onTextTypeChange(selectedType);
      }
    },
    [editor, onTextTypeChange]
  );

  const handleFontSizeSelection = useCallback(
    (event) => {
      const selectedSize = Number(event?.target?.value || 16);
      setFontSize(selectedSize);
      if (editor?.chain) {
        editor.chain().focus().setFontSize(String(selectedSize)).run();
      }
      if (onFontSizeChange) {
        onFontSizeChange(selectedSize);
      }
    },
    [editor, onFontSizeChange]
  );

  const readFileAsDataUrl = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(new Error('Unable to read selected image.'));
        reader.readAsDataURL(file);
      }),
    []
  );

  const handleImageUpload = useCallback(
    async (event) => {
      const file = event?.target?.files?.[0];
      if (!file) {
        return;
      }

      const isAllowedType = Object.keys(IMAGE_ACCEPT).includes(file?.type);
      if (!isAllowedType) {
        swalToast({
          icon: 'error',
          title:
            'Please upload image in valid format. Accepted formats include .png, .svg, .jpeg, .jpg',
        });
        const targetInput = event?.target;
        if (targetInput) {
          targetInput.value = '';
        }
        return;
      }

      if (file?.size > MAX_IMAGE_SIZE) {
        swalToast({
          icon: 'error',
          title: 'Image size should not exceed 5MB.',
        });
        const input = event?.target;
        if (input) {
          input.value = '';
        }
        return;
      }

      try {
        const base64Image = await readFileAsDataUrl(file);
        if (editor?.chain) {
          editor.chain().focus().setImage({ src: base64Image }).run();
        }
        if (onImageSelected) {
          onImageSelected({
            file,
            base64: base64Image,
            type: file?.type,
            name: file?.name,
          });
        }
      } catch (error) {
        swalToast({
          icon: 'error',
          title: error?.message || 'Failed to process image.',
        });
      } finally {
        const targetInput = event?.target;
        if (targetInput) {
          targetInput.value = '';
        }
      }
    },
    [editor, onImageSelected, readFileAsDataUrl]
  );

  const getIconButtonSx = (isActive) => ({
    bgcolor: isActive ? theme.palette.action.selected : 'transparent',
    '&:hover': {
      bgcolor: isActive
        ? theme.palette.action.selected
        : theme.palette.action.hover,
    },
  });

  const handleBoldMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      if (editor?.chain) {
        editor.chain().focus().toggleBold().run();
      }
      if (onToggleBold) {
        onToggleBold();
      }
    },
    [editor, onToggleBold]
  );

  const handleItalicMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      if (editor?.chain) {
        editor.chain().focus().toggleItalic().run();
      }
      if (onToggleItalic) {
        onToggleItalic();
      }
    },
    [editor, onToggleItalic]
  );

  const handleUnderlineMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      if (editor?.chain) {
        editor.chain().focus().toggleUnderline().run();
      }
      if (onToggleUnderline) {
        onToggleUnderline();
      }
    },
    [editor, onToggleUnderline]
  );

  const handleBulletListMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      if (editor?.chain) {
        editor.chain().focus().toggleBulletList().run();
      }
      if (onToggleBulletList) {
        onToggleBulletList();
      }
    },
    [editor, onToggleBulletList]
  );

  const handleOrderedListMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      if (editor?.chain) {
        editor.chain().focus().toggleOrderedList().run();
      }
      if (onToggleOrderedList) {
        onToggleOrderedList();
      }
    },
    [editor, onToggleOrderedList]
  );

  const handleAlignLeft = useCallback(() => {
    if (editor?.chain) {
      editor.chain().focus().setTextAlign('left').run();
    }
    if (onTextAlignChange) {
      onTextAlignChange('left');
    }
  }, [editor, onTextAlignChange]);

  const handleAlignCenter = useCallback(() => {
    if (editor?.chain) {
      editor.chain().focus().setTextAlign('center').run();
    }
    if (onTextAlignChange) {
      onTextAlignChange('center');
    }
  }, [editor, onTextAlignChange]);

  const handleAlignRight = useCallback(() => {
    if (editor?.chain) {
      editor.chain().focus().setTextAlign('right').run();
    }
    if (onTextAlignChange) {
      onTextAlignChange('right');
    }
  }, [editor, onTextAlignChange]);

  const handleInsertTextBox = useCallback(() => {
    if (onInsertTextBox) {
      onInsertTextBox();
    }
  }, [onInsertTextBox]);

  const handleToolbarCollapse = useCallback(() => {
    if (onToggleToolbarCollapse) {
      onToggleToolbarCollapse();
    }
  }, [onToggleToolbarCollapse]);

  const handleUndoClick = useCallback(() => {
    if (editor?.chain) {
      editor.chain().focus().undo().run();
    }
    if (onUndo) {
      onUndo();
    }
  }, [editor, onUndo]);

  const handleRedoClick = useCallback(() => {
    if (editor?.chain) {
      editor.chain().focus().redo().run();
    }
    if (onRedo) {
      onRedo();
    }
  }, [editor, onRedo]);

  const isBoldActive = Boolean(
    editor?.isActive?.('bold') || activeTextStyle?.bold
  );
  const isItalicActive = Boolean(
    editor?.isActive?.('italic') || activeTextStyle?.italic
  );
  const isUnderlineActive = Boolean(
    editor?.isActive?.('underline') || activeTextStyle?.underline
  );
  const isBulletListActive = Boolean(
    editor?.isActive?.('bulletList') || activeTextStyle?.bulletList
  );
  const isOrderedListActive = Boolean(
    editor?.isActive?.('orderedList') || activeTextStyle?.orderedList
  );
  const currentAlign = activeTextStyle?.textAlign || 'left';

  const renderActionButtons = () => {
    if (mode === 'lifecycle') {
      return null;
    }
    if (mode === 'Edit') {
      return (
        <SolyticsBox
          sx={{
            display: 'flex !important',
            flexDirection: 'row !important',
            justifyContent: 'end',
            paddingTop: '2px',
          }}
        >
          <SolyticsBox
            variant="contained"
            disabled={isEditorEmpty}
            sx={{
              fontSize: '10px',
              height: 1,
              minWidth: 2,
              fontWeight: 700,
              cursor: isEditorEmpty ? 'default' : 'pointer',
            }}
            title="Cancel"
          >
            {cancelButton}
          </SolyticsBox>
          <SolyticsBox
            size="small"
            variant="contained"
            disabled={isEditorEmpty}
            sx={{
              fontSize: '10px',
              height: 1,
              minWidth: 2,
              fontWeight: 700,
              cursor: isEditorEmpty ? 'default' : 'pointer',
            }}
            title="Submit"
          >
            {submitButton}
          </SolyticsBox>
        </SolyticsBox>
      );
    }

    let buttonTitle = 'Send';
    if (!isUserAuthorizedForAddNote) {
      buttonTitle = NOT_AUTHORIZED_TO_ACCESS_MESSAGE;
    } else if (isEditorEmpty) {
      buttonTitle = 'Add a message before submitting.';
    }

    return (
      <SolyticsIconButton
        size="small"
        variant="contained"
        color="primary"
        disabled={isEditorEmpty || !isUserAuthorizedForAddNote}
        onClick={handleSubmit}
        sx={{
          fontSize: '10px',
          height: 1,
          minWidth: 2,
          fontWeight: 700,
          ml: 'auto',
          cursor: isEditorEmpty ? 'default' : 'pointer',
          alignSelf: 'center',
        }}
        title={buttonTitle}
        aria-label={buttonTitle}
      >
        <SendIcon />
      </SolyticsIconButton>
    );
  };

  if (!editor && !canRenderWithoutEditor) {
    return null;
  }

  const blockTypeOptions = BLOCK_TYPES.map((type) => ({
    label: type.label,
    value: type.label,
  }));

  const fontSizeOptions = FONT_SIZE_OPTIONS.map((size) => ({
    label: String(size),
    value: size,
  }));

  return (
    <SolyticsBox
      className="editor-toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        p: 1,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        bgcolor: theme.palette.background.paper,
      }}
    >
      <SolyticsBox
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          flexGrow: 1,
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Undo"
          onClick={handleUndoClick}
          sx={getIconButtonSx(false)}
        >
          <UndoIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Redo"
          onClick={handleRedoClick}
          sx={getIconButtonSx(false)}
        >
          <SolyticsBox sx={{ transform: 'scaleX(-1)', display: 'flex' }}>
            <UndoIcon width={20} height={20} />
          </SolyticsBox>
        </SolyticsIconButton>
        <SolyticsDivider orientation="vertical" flexItem variant="middle" />
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Insert text box"
          onClick={handleInsertTextBox}
        >
          <TextBoxIcon width={20} height={20} />
        </SolyticsIconButton>
        {!hideInsertImageButton && (
          <SolyticsIconButton
            color="inherit"
            size="small"
            title="Insert image"
            onClick={handleImageIconClick}
          >
            <AttachmentIcon width={20} height={20} />
          </SolyticsIconButton>
        )}
        <SolyticsDivider orientation="vertical" flexItem variant="middle" />
        <SolyticsBox sx={{ width: '160px' }}>
          <SolyticsDropdownInput
            name="textType"
            size="small"
            variant="basic"
            options={blockTypeOptions}
            value={textType.label || 'Normal text'}
            onChange={handleTextTypeChange}
            placeholder="Text Type"
          />
        </SolyticsBox>

        <SolyticsDivider orientation="vertical" flexItem variant="middle" />
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Bold"
          onClick={handleBoldMouseDown}
          sx={getIconButtonSx(isBoldActive)}
        >
          <BoldIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Italic"
          onClick={handleItalicMouseDown}
          sx={getIconButtonSx(isItalicActive)}
        >
          <ItalicIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Underline"
          onClick={handleUnderlineMouseDown}
          sx={getIconButtonSx(isUnderlineActive)}
        >
          <UnderlineIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsDivider orientation="vertical" flexItem variant="middle" />
        <SolyticsDropdownInput
          name="fontSize"
          size="small"
          variant="basic"
          options={fontSizeOptions}
          value={fontSize}
          onChange={handleFontSizeSelection}
          minWidth={90}
          placeholder="16"
          disabled={isFormattingDisabled}
        />
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Align left"
          onClick={handleAlignLeft}
          sx={getIconButtonSx(currentAlign === 'left')}
          disabled={isFormattingDisabled}
        >
          <AlignLeftIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Align center"
          onClick={handleAlignCenter}
          sx={getIconButtonSx(currentAlign === 'center')}
          disabled={isFormattingDisabled}
        >
          <AlignCenterIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsIconButton
          color="inherit"
          size="small"
          title="Align right"
          onClick={handleAlignRight}
          sx={getIconButtonSx(currentAlign === 'right')}
          disabled={isFormattingDisabled}
        >
          <AlignRightIcon width={20} height={20} />
        </SolyticsIconButton>
        <SolyticsDivider orientation="vertical" flexItem variant="middle" />
        {showListControls && (
          <>
            <SolyticsIconButton
              color="inherit"
              size="small"
              title="Bullet list"
              onClick={handleBulletListMouseDown}
              sx={getIconButtonSx(isBulletListActive)}
              disabled={isFormattingDisabled}
            >
              <BulletListIcon width={20} height={20} />
            </SolyticsIconButton>
            <SolyticsIconButton
              color="inherit"
              size="small"
              title="Numbered list"
              onClick={handleOrderedListMouseDown}
              sx={getIconButtonSx(isOrderedListActive)}
              disabled={isFormattingDisabled}
            >
              <OrderedListIcon width={20} height={20} />
            </SolyticsIconButton>
          </>
        )}

        <input
          type="file"
          accept={IMAGE_ACCEPT_ATTR}
          ref={imageInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        {showAttachment && (
          <>
            <SolyticsDivider orientation="vertical" flexItem variant="middle" />
            <SolyticsIconButton
              color="inherit"
              size="small"
              title="Add attachments"
              {...addAttachmentsGetRootProps()}
              onClick={openAddAttachments}
            >
              <AddIcon width={18} height={18} />
              <input
                style={{ display: 'none' }}
                {...addAttachmentsGetInputProps()}
              />
            </SolyticsIconButton>
          </>
        )}
        {renderActionButtons()}
      </SolyticsBox>
      {showCollapseControl && (
        <SolyticsButton
          variant="text"
          onClick={handleToolbarCollapse}
          title={isToolbarCollapsed ? 'Expand toolbar' : 'Collapse toolbar'}
        >
          <SolyticsBox
            sx={{
              display: 'flex',
              transform: isToolbarCollapsed ? 'rotate(180deg)' : 'none',
            }}
          >
            <CollapseChevronIcon width={18} height={18} />
          </SolyticsBox>
          <SolyticsTypography variant="caption" sx={{ ml: 0.5 }}>
            {isToolbarCollapsed ? 'Expand' : 'Collapse'}
          </SolyticsTypography>
        </SolyticsButton>
      )}
    </SolyticsBox>
  );
};

EditorToolBar.propTypes = {
  editor: PropTypes.oneOfType([PropTypes.object]),
  handleAttachFile: PropTypes.func,
  isEditorEmpty: PropTypes.bool,
  handleSubmit: PropTypes.func,
  mode: PropTypes.string,
  cancelButton: PropTypes.node,
  submitButton: PropTypes.node,
  isUserAuthorizedForAddNote: PropTypes.bool,
  showAttachment: PropTypes.bool,
  hideInsertImageButton: PropTypes.bool,
  allowStandalone: PropTypes.bool,
  onInsertTextBox: PropTypes.func,
  onImageSelected: PropTypes.func,
  onTextAlignChange: PropTypes.func,
  onFontSizeChange: PropTypes.func,
  onToggleBold: PropTypes.func,
  onToggleItalic: PropTypes.func,
  onToggleUnderline: PropTypes.func,
  onToggleOrderedList: PropTypes.func,
  onToggleBulletList: PropTypes.func,
  onTextTypeChange: PropTypes.func,
  activeTextStyle: PropTypes.shape({
    fontSize: PropTypes.number,
    bold: PropTypes.bool,
    italic: PropTypes.bool,
    underline: PropTypes.bool,
    textAlign: PropTypes.string,
    bulletList: PropTypes.bool,
    orderedList: PropTypes.bool,
  }),
  showCollapseControl: PropTypes.bool,
  isToolbarCollapsed: PropTypes.bool,
  onToggleToolbarCollapse: PropTypes.func,
  isFormattingDisabled: PropTypes.bool,
  showListControls: PropTypes.bool,
  onUndo: PropTypes.func,
  onRedo: PropTypes.func,
};

export default EditorToolBar;
